/**
 * Which events are folded out, remembered between sessions.
 *
 * ---------------------------------------------------------------------------------------------
 * NAVIGATION.md: "Flere arrangementer kan være utfoldet samtidig. Utfoldingstilstand og sist
 * besøkte side huskes per bruker mellom økter." A SET of ids, therefore, not one open row — and
 * it survives a reload, so a production that works in two festivals at once opens the browser
 * with both of them already open.
 *
 * LOCAL, LIKE THE THEME AND UNLIKE THE LANGUAGE (00-contracts §14.1). Which rows you left folded
 * out is a property of this browser at this desk, not of you as a user in the database: it has
 * no server field, it must not cost a round trip on every click, and getting it wrong loses
 * nothing but one arrow press. `localStorage`, wrapped, exactly as `auth/active-org.ts` and the
 * theme do — and every read and write in a `try`, because private mode and blocked storage are
 * real and a menu that throws is worse than a menu that forgets.
 *
 * IDS RATHER THAN INDEXES OR SLUGS. A festival id is a UUIDv7 and unique across every
 * organisation, so a stored id from an organisation you have since switched away from simply
 * never matches a row and is never rendered. That is also what makes "per bruker" true enough
 * for a shared browser: two people signed in at the same desk cannot collide on an id, and
 * neither of them can learn anything from a set of ids for events they cannot see — the list
 * they are matched against is the permission-filtered one from the API.
 *
 * READ ON MOUNT, NOT KEPT IN A MODULE. The hook's state is seeded from storage every time it
 * mounts. A module-level cache would make a remount look like it remembered when storage was in
 * fact broken, which is precisely the thing this file has to be honest about.
 * ---------------------------------------------------------------------------------------------
 */
import { useCallback, useState } from 'react';

export const EXPANDED_EVENTS_STORAGE_KEY = 'tvitvi.sidebar.expandedEvents';

function readStored(): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(EXPANDED_EVENTS_STORAGE_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === 'string'));
  } catch {
    // Blocked storage, or a value somebody else wrote. An empty set is a correct menu.
    return new Set();
  }
}

function writeStored(ids: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(EXPANDED_EVENTS_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // See `readStored`: the fold still applies for this session, it just will not survive.
  }
}

export interface ExpandedEvents {
  isExpanded: (eventId: string) => boolean;
  /** The arrow: folds out what is folded in, and in what is out. */
  toggle: (eventId: string) => void;
  /** The name: opens the event AND folds it out, never folds it back in (NAVIGATION.md). */
  expand: (eventId: string) => void;
}

export function useExpandedEvents(): ExpandedEvents {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() =>
    typeof window === 'undefined' ? new Set<string>() : readStored(),
  );

  const isExpanded = useCallback((eventId: string) => expanded.has(eventId), [expanded]);

  const toggle = useCallback((eventId: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(eventId)) next.add(eventId);
      writeStored(next);
      return next;
    });
  }, []);

  const expand = useCallback((eventId: string) => {
    setExpanded((current) => {
      if (current.has(eventId)) return current;
      const next = new Set(current).add(eventId);
      writeStored(next);
      return next;
    });
  }, []);

  return { isExpanded, toggle, expand };
}
